import type { CircuitPathResult } from "../../../ReviewPage/hooks/useCircuitCablePaths/types";
import type { CableTraySegment } from "../../utils/fetchCableTakeoffPaths";
import type {
  FingerprintCircuit,
  FingerprintRouterInputs,
  FingerprintTrays,
} from "./types";

/**
 * Two decimal places, matching `circuitMetres`' own segment precision. Float
 * noise from re-deriving the same geometry must not read as an edit.
 */
const PRECISION = 2;
const SCALE = 10 ** PRECISION;

/**
 * Round BEFORE formatting, or a coordinate a hair below zero prints "-0.00"
 * where the same coordinate a hair above prints "0.00" - two fingerprints for
 * one cable, and a run permanently marked stale. Rounding first collapses both
 * onto -0, which `toFixed` renders unsigned.
 */
const at = (value: number): string =>
  (Math.round(value * SCALE) / SCALE).toFixed(PRECISION);

/**
 * A stable digest of what a circuit's cable IS, so a stored billed figure can
 * say whether it still describes the drawing.
 *
 * Keyed on the paths rather than on the edits that produced them: an edit and
 * its undo leave the geometry where it started, and a run nobody has changed is
 * not stale however many times he opened the card. Sorted by destination so the
 * order paths happen to be derived in cannot register as a change.
 *
 * FNV-1a, not a cryptographic hash: this compares two values we computed
 * ourselves moments apart, and the cost of a collision is one un-flagged
 * recalculate, not a wrong number.
 */
export const fingerprintCircuit: FingerprintCircuit = (paths) => {
  const parts = [...paths]
    .sort((a, b) => (a.destinationId < b.destinationId ? -1 : 1))
    .map(
      (p: CircuitPathResult) =>
        `${p.destinationId}:` +
        p.waypoints.map((w) => `${at(w.x)},${at(w.y)}`).join(" "),
    );

  let hash = 0x811c9dc5;
  const text = parts.join("|");
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  // Length alongside the hash: cheap, and it makes the only realistic collision
  // (two runs of different size) impossible rather than merely unlikely.
  return `${(hash >>> 0).toString(36)}-${text.length.toString(36)}`;
};

/**
 * A digest of the tray network the router would read.
 *
 * The per-circuit fingerprint watches CABLE geometry, and a tray edit does not
 * touch a single cable waypoint — so drawing a new tray would leave every run
 * looking fresh while the metres it implies had already changed. This is the
 * other half: it marks the PLAN, which is the right scope anyway, because
 * welding is global and one new edge can change which candidate network wins
 * for circuits nowhere near it.
 *
 * Sorted and direction-agnostic: a tray is the same steel whichever end it was
 * drawn from, and the order routes come back in is not a change.
 */
export const fingerprintTrays: FingerprintTrays = (trays) => {
  const parts = trays
    .map((t: CableTraySegment) => {
      const a = `${at(t.from.x)},${at(t.from.y)}`;
      const b = `${at(t.to.x)},${at(t.to.y)}`;
      return `${t.type}:${a < b ? `${a}>${b}` : `${b}>${a}`}`;
    })
    .sort();

  let hash = 0x811c9dc5;
  const text = parts.join("|");
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${(hash >>> 0).toString(36)}-${text.length.toString(36)}`;
};

/**
 * A digest of everything the router reads about the BUILDING — the trays and the
 * walls.
 *
 * Walls belong in here for exactly the reason trays do: a wall he draws changes
 * where cable can go, and the per-circuit fingerprint watches CABLE geometry, so
 * drawing one would otherwise leave every run looking fresh while the metres it
 * implies had already changed. *"Again I drew a wall but recalculate button is
 * not showing up."* — the button was there; nothing was telling him to press it.
 *
 * With no walls drawn it is the tray fingerprint, byte for byte, so a plan that
 * has never had a wall on it does not go stale the day this shipped.
 */
export const fingerprintRouterInputs: FingerprintRouterInputs = ({
  trays,
  walls,
}) => {
  const trayPart = fingerprintTrays(trays);
  if (!walls || walls.length === 0) return trayPart;

  // Rounded through the same `at` this file uses for cable, so float noise from
  // re-deriving one drawing does not read as a wall he moved. Sorted, because
  // the order rows come back in is not a change.
  const parts = walls
    .map(
      (wall) =>
        `${wall.kind}:` +
        wall.points.map((point) => `${at(point.x)},${at(point.y)}`).join(" "),
    )
    .sort();

  let hash = 0x811c9dc5;
  const text = parts.join("|");
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${trayPart}~${(hash >>> 0).toString(36)}-${text.length.toString(36)}`;
};
